import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import {
    ProfitChart, WinLossChart, CategoryPieChart, OddsDistributionChart,
    CumulativeProfitChart, ChainBreakdownChart, ActivityHeatmap,
} from '../charts/AnalyticsCharts';
import {
    TrendingUp, BarChart3, PieChart, Target, LineChart as LineChartIcon,
    Globe, CalendarDays, Activity, Timer, ArrowUpRight, ArrowDownRight, ChevronRight, History,
} from 'lucide-react';

export function DashboardAnalyticsTab({ stats, loading }) {
    if (loading) return <Skeleton className="h-96 bg-white/5" />;
    if (!stats) return null;

    return (
        <div className="space-y-6">
            <Card className="bg-white/[0.02] border-white/5">
                <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-[#00FF66]" />
                        Monthly Profit/Loss
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <ProfitChart data={stats.monthly_stats} />
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-white/[0.02] border-white/5">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-[#FFD700]" />
                            Wins vs Losses
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <WinLossChart data={stats.monthly_stats?.map(m => ({
                            month: m.month, wins: m.wins || 0, losses: (m.bets || 0) - (m.wins || 0),
                        }))} />
                    </CardContent>
                </Card>

                <Card className="bg-white/[0.02] border-white/5">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <PieChart className="w-5 h-5 text-[#00FF66]" />
                            Category Distribution
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <CategoryPieChart data={stats.category_breakdown} />
                    </CardContent>
                </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-white/[0.02] border-white/5">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <Target className="w-5 h-5 text-[#9B6FE8]" />
                            Risk Profile
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <OddsDistributionChart data={stats.odds_preference} />
                    </CardContent>
                </Card>

                <Card className="bg-white/[0.02] border-white/5">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <LineChartIcon className="w-5 h-5 text-[#FFD700]" />
                            Cumulative Profit
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <CumulativeProfitChart data={stats.monthly_stats} />
                    </CardContent>
                </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-white/[0.02] border-white/5 animate-fade-in-up">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <Globe className="w-5 h-5 text-[#0052FF]" />
                            P&L by Chain
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ChainBreakdownChart data={stats.chain_breakdown} />
                    </CardContent>
                </Card>

                <Card className="bg-white/[0.02] border-white/5 animate-fade-in-up">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <CalendarDays className="w-5 h-5 text-[#00FF66]" />
                            Betting Activity
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ActivityHeatmap data={stats.weekly_activity} />
                    </CardContent>
                </Card>
            </div>

            <Card className="bg-white/[0.02] border-white/5">
                <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                        <Activity className="w-5 h-5 text-[#00FF66]" />
                        Performance Summary
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Object.entries(stats.category_breakdown).map(([category, data]) => (
                            <div key={category} className="p-4 rounded-lg bg-white/[0.02] border border-white/5">
                                <div className="text-xs text-white/40 uppercase mb-1">{category}</div>
                                <div className="text-white font-semibold">{data.count} bets</div>
                                <div className="flex justify-between items-center mt-2">
                                    <span className="text-xs text-white/40">{data.wins} wins</span>
                                    <span className={`font-mono text-xs ${data.profit >= 0 ? 'text-[#00FF66]' : 'text-red-400'}`}>
                                        {data.profit >= 0 ? '+' : ''}{data.profit.toFixed(3)}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export function DashboardActiveBetsTab({ stats, loading }) {
    if (loading) return <Skeleton className="h-64 bg-white/5" />;
    if (!stats?.active_bets?.length) {
        return (
            <div className="p-16 rounded-2xl bg-white/[0.02] border border-white/5 text-center">
                <Timer className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Active Bets</h3>
                <p className="text-white/50 mb-6">You don't have any active bets at the moment.</p>
                <Link to="/bets">
                    <Button className="bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52]">Browse Open Bets</Button>
                </Link>
            </div>
        );
    }
    return (
        <div className="space-y-4">
            {stats.active_bets.map((bet) => (
                <Link key={bet.id} to={`/bets/${bet.id}`}>
                    <Card className="bg-white/[0.02] border-white/5 hover:border-[#00FF66]/30 transition-colors cursor-pointer">
                        <CardContent className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${bet.is_creator ? 'bg-[#00FF66]/20' : 'bg-[#6F3FCA]/20'}`}>
                                    {bet.is_creator ? (
                                        <ArrowUpRight className="w-5 h-5 text-[#00FF66]" />
                                    ) : (
                                        <ArrowDownRight className="w-5 h-5 text-[#9B6FE8]" />
                                    )}
                                </div>
                                <div>
                                    <p className="text-white font-medium">{bet.description}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                        <Badge className="bg-white/5 text-white/60 text-xs capitalize">{bet.status}</Badge>
                                        <span className="text-white/40 text-xs">{bet.is_creator ? 'Created' : 'Taken'}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="text-right">
                                    <div className="text-white font-mono">{bet.stake.toFixed(4)} ETH</div>
                                    <div className="text-white/40 text-xs">Your stake</div>
                                </div>
                                <ChevronRight className="w-5 h-5 text-white/30" />
                            </div>
                        </CardContent>
                    </Card>
                </Link>
            ))}
        </div>
    );
}

export function DashboardHistoryTab() {
    return (
        <div className="p-8 rounded-2xl bg-white/[0.02] border border-white/5 text-center">
            <History className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Full History</h3>
            <p className="text-white/50 mb-6">View your complete betting history with filters and exports.</p>
            <Link to="/history">
                <Button variant="outline" className="border-white/10 text-white hover:bg-white/5">
                    View Full History
                    <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
            </Link>
        </div>
    );
}
